import React from 'react';
import './Features.css';

const coreFeatures = [
  {
    icon: '📄',
    title: 'AI Document Intake',
    description: 'Turn supplier PDFs, client PO photos, delivery notes, and invoices into structured records — with human review and learning aliases.',
    benefits: ['PDF, image, and spreadsheet imports', 'Human-in-the-loop review', 'Aliases improve over time']
  },
  {
    icon: '💰',
    title: 'Dynamic Pricing',
    description: 'Margin-from-cost or fixed list-price mode with client-specific price lists, discount rules, and approval thresholds.',
    benefits: ['Margin or list-price modes', 'Client-specific price lists', 'Bulk quantity discounts']
  },
  {
    icon: '🛒',
    title: 'Sales & Procurement',
    description: 'Capture and approve sales orders, manage purchase orders, reorder alerts, and optional AI reorder agents that email suppliers for quotes.',
    benefits: ['Tiered SO approvals', 'SO addendums for revisions', 'PO mirroring via Network']
  },
  {
    icon: '📦',
    title: 'Warehouse Operations',
    description: 'Receiving, put-away, pick, pack, assembly, and delivery orders — plus a floor PWA with offline sync and stock lookup.',
    benefits: ['Full WMS lifecycle', 'Mobile PWA for floor staff', 'Kanban delivery board']
  },
  {
    icon: '🧾',
    title: 'Smart Invoicing',
    description: 'Branded PDF templates, scheduled billing, collections aging, and client delivery receipt matching to trigger invoices.',
    benefits: ['Custom invoice templates', 'AR collections tracking', 'Receipt-to-invoice matching']
  },
  {
    icon: '🔗',
    title: 'Neoshift Network',
    description: 'Connect trading partners on Neoshift — POs mirror to supplier SOs and goods receipts forward for invoicing without email attachments.',
    benefits: ['Automated PO → SO sync', 'Receipt-to-invoice flow', 'Federated Discuss threads']
  }
];

const etlFeatures = [
  {
    icon: '📄',
    title: 'Read & Extract',
    description: 'Ingest any document format — POs, invoices, delivery orders, contracts, and more. If a human can read it, our AI can extract it. No pre-formatting needed.',
    benefits: ['Any format, any layout', 'Advanced OCR & AI extraction', 'Handles complex documents']
  },
  {
    icon: '✅',
    title: 'AI Validation',
    description: 'Automatically validate extracted data against your business rules and company records. Catch errors before they enter your system — not after.',
    benefits: ['Business rule validation', 'Cross-reference records', 'Eliminates 8-12% manual error rate']
  },
  {
    icon: '🔄',
    title: 'Multi-System Sync',
    description: 'Push validated data directly into your ERP, CRM, WMS, and other systems instantly. Solve fragmentation by keeping all platforms in sync automatically.',
    benefits: ['ERP, CRM, WMS integration', 'Real-time synchronization', 'Eliminates manual data entry']
  },
  {
    icon: '⚙️',
    title: 'B2B Operations Automation',
    description: 'Automate the complete B2B operations cycle — from procurement and order processing to invoicing, payments, and delivery reconciliation.',
    benefits: ['PO & Invoice processing', 'Payment & delivery tracking', 'Contract management']
  },
  {
    icon: '🔗',
    title: 'Seamless Integration',
    description: 'Our AI runs invisibly on top of your existing stack. No disruption, no migration — your team keeps using familiar tools while we automate the painful parts.',
    benefits: ['Plug-and-play deployment', 'No workflow disruption', 'Works with existing software']
  },
  {
    icon: '🛡️',
    title: 'Enterprise Security',
    description: 'Bank-grade security with end-to-end encryption, compliance standards, and secure data handling to protect your sensitive business documents.',
    benefits: ['End-to-end encryption', 'Confidential data handling', 'Compliance-ready']
  }
];

const biFeatures = [
  {
    icon: '🤖',
    title: 'AI-Powered Analytics',
    description: 'Ask questions in plain English and get instant answers from your data. Our AI detects trends, generates insights, and forecasts time-series data.',
    benefits: ['Natural language queries', 'Automatic insights', 'Trend detection']
  },
  {
    icon: '📊',
    title: 'Interactive Dashboards',
    description: 'Build beautiful, interactive dashboards with our drag-and-drop builder. Visualize your data with bar, line, pie, area, scatter, and heatmap charts.',
    benefits: ['Drag-and-drop builder', 'Real-time updates', 'Apache ECharts']
  },
  {
    icon: '🔗',
    title: 'AI Data Mapping',
    description: 'Use AI to discover relationships between your data sources. Create views by joining tables and adding calculated columns — no SQL required.',
    benefits: ['Auto relationship discovery', 'Visual table joins', 'Calculated columns']
  },
  {
    icon: '✨',
    title: 'AI Chart Builder',
    description: 'Describe the data you want to see and our AI creates the perfect chart. From simple metrics to complex visualizations, no coding needed.',
    benefits: ['Natural language input', 'Smart chart selection', 'Instant generation']
  },
  {
    icon: '🔌',
    title: 'Multi-Source Data',
    description: 'Connect to CSV, Excel, REST APIs, PostgreSQL, MySQL, and Google Sheets. Import, schedule syncs, and unify all your data in one place.',
    benefits: ['6+ data connectors', 'Scheduled imports', 'Schema detection']
  },
  {
    icon: '🛡️',
    title: 'Enterprise Security',
    description: 'Built for enterprise with multi-tenant architecture, role-based access control, row-level security, and comprehensive audit logging.',
    benefits: ['5 user roles', 'Row-level security', 'Audit logging']
  }
];

const Features = ({ selectedProduct, onContactClick }) => {
  const features =
    selectedProduct === 'bi' ? biFeatures :
    selectedProduct === 'core' ? coreFeatures :
    etlFeatures;

  const title =
    selectedProduct === 'bi' ? 'Intelligent Features for Business Intelligence' :
    selectedProduct === 'core' ? 'One Platform for Order-to-Cash & Procure-to-Pay' :
    'How Neoshift Replaces Manual Work';

  const subtitle =
    selectedProduct === 'bi'
      ? 'Neoshift BI combines AI analytics with intuitive visualization tools to help you understand your data and make smarter decisions.'
      : selectedProduct === 'core'
      ? 'Neoshift Core gives each company its own isolated workspace for the full operations cycle — from supplier PDFs to branded invoices and warehouse fulfillment.'
      : 'Our AI reads, validates, and syncs your documents automatically — turning hours of manual processing into seconds with up to 80% reduction in human touchpoints.';

  return (
    <section id="features" className="features">
      <div className="features-container">
        <div className="features-header">
          <h2 className="features-title">{title}</h2>
          <p className="features-subtitle">{subtitle}</p>
        </div>

        <div className="features-grid" key={selectedProduct}>
          {features.map((feature, index) => (
            <div key={index} className="feature-card" data-aos="fade-up" data-aos-delay={index * 100}>
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
              <ul className="feature-benefits">
                {feature.benefits.map((benefit, benefitIndex) => (
                  <li key={benefitIndex} className="feature-benefit">
                    <span className="benefit-check">✓</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="features-cta">
          {selectedProduct === 'core' ? (
            <>
              <h3>Ready to Run Your Back Office on Neoshift Core?</h3>
              <p>Launch your workspace or talk to our team about a guided rollout for distributors and wholesalers.</p>
              <div className="features-cta-actions">
                <a
                  href="https://core.neoshift.ai"
                  className="features-cta-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Launch Neoshift Core
                </a>
                <button className="features-cta-button features-cta-secondary" onClick={onContactClick}>
                  Book a Demo
                </button>
              </div>
            </>
          ) : selectedProduct === 'bi' ? (
            <>
              <h3>Ready to Unlock Your Data's Potential?</h3>
              <p>Start analyzing your data with AI-powered insights. Launch Neoshift BI today.</p>
              <div className="features-cta-actions">
                <a
                  href="https://bi.neoshift.ai"
                  className="features-cta-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Launch Neoshift BI
                </a>
                <button className="features-cta-button features-cta-secondary" onClick={onContactClick}>
                  Contact Sales
                </button>
              </div>
            </>
          ) : (
            <>
              <h3>See It Work on Your Data — Free</h3>
              <p>We'll build a complimentary Proof-of-Concept using your actual documents so you can see the results before committing.</p>
              <button className="features-cta-button" onClick={onContactClick}>Get Your Free POC</button>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default Features;
